import { useState } from 'react'
import {
  Card, Table, Button, Space, Typography, Tag, Row, Col,
  Select, DatePicker, Alert, message,
} from 'antd'
import { BarChartOutlined, DownloadOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Title, Text } = Typography
const API = ''

function fmtINR(v: number | undefined | null) {
  if (v == null || v === 0) return ''
  return `₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

const GROUP_TYPE_ORDER = [
  'ASSET_NON_CURRENT', 'ASSET_CURRENT',
  'LIABILITY_NON_CURRENT', 'LIABILITY_CURRENT',
  'EQUITY', 'INCOME', 'DIRECT_COST', 'INDIRECT_COST',
]

const GROUP_TYPE_LABELS: Record<string, string> = {
  ASSET_NON_CURRENT: 'Non-Current Assets',
  ASSET_CURRENT: 'Current Assets',
  LIABILITY_NON_CURRENT: 'Non-Current Liabilities',
  LIABILITY_CURRENT: 'Current Liabilities',
  EQUITY: 'Equity',
  INCOME: 'Income',
  DIRECT_COST: 'Direct Costs',
  INDIRECT_COST: 'Indirect Costs / Expenses',
}

function exportCSV(rows: Record<string, unknown>[], filename: string) {
  const headers = ['Group', 'Ledger Code', 'Ledger Name', 'DR Balance', 'CR Balance']
  const lines = [
    headers.join(','),
    ...rows.map((r: Record<string, unknown>) => [
      r.group_type ?? '',
      r.ledger_code ?? '',
      `"${r.ledger_name ?? ''}"`,
      r.dr_balance ?? '',
      r.cr_balance ?? '',
    ].join(',')),
  ]
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

export default function TrialBalancePage() {
  const { plantId, plants, setPlantId } = usePlantId()

  const [period, setPeriod] = useState<dayjs.Dayjs>(dayjs())
  const [groupFilter, setGroupFilter] = useState<string | undefined>()
  const [triggered, setTriggered] = useState(false)

  const { data: tbData, isLoading, refetch } = useQuery({
    queryKey: ['trial-balance', plantId, period?.format('YYYY-MM'), groupFilter],
    queryFn: () =>
      apiClient.get(`${API}/accounting/trial-balance`, {
        params: {
          plant_id: plantId,
          month: period?.month() + 1,
          year: period?.year(),
          group_type: groupFilter || undefined,
        },
      }).then(r => r.data),
    enabled: !!plantId && triggered,
  })

  const handleGenerate = () => {
    if (!plantId) { message.warning('Select a plant first'); return }
    setTriggered(true)
    refetch()
  }

  const entries = Array.isArray(tbData?.entries ?? tbData) ? (tbData?.entries ?? tbData) : []
  const totalDR = tbData?.total_dr ?? entries.reduce((s: number, e: any) => s + Number(e.dr_balance ?? 0), 0)
  const totalCR = tbData?.total_cr ?? entries.reduce((s: number, e: any) => s + Number(e.cr_balance ?? 0), 0)
  const isBalanced = Math.abs(totalDR - totalCR) < 1

  // Group entries
  type GroupMap = Record<string, { entries: any[]; subtotalDR: number; subtotalCR: number }>
  const grouped: GroupMap = {}
  entries.forEach((e: any) => {
    const g = e.group_type ?? 'OTHER'
    if (!grouped[g]) grouped[g] = { entries: [], subtotalDR: 0, subtotalCR: 0 }
    grouped[g].entries.push(e)
    grouped[g].subtotalDR += Number(e.dr_balance ?? 0)
    grouped[g].subtotalCR += Number(e.cr_balance ?? 0)
  })

  // Build flat table data for display
  const tableRows: any[] = []
  const orderedGroups = [
    ...GROUP_TYPE_ORDER.filter(g => grouped[g]),
    ...Object.keys(grouped).filter(g => !GROUP_TYPE_ORDER.includes(g)),
  ]

  orderedGroups.forEach((g) => {
    const { entries: gEntries, subtotalDR, subtotalCR } = grouped[g]
    // Group header row
    tableRows.push({
      key: `header-${g}`,
      isHeader: true,
      ledger_name: GROUP_TYPE_LABELS[g] ?? g,
      group_type: g,
      dr_balance: null,
      cr_balance: null,
    })
    gEntries.forEach((e: any) => {
      tableRows.push({ ...e, key: e.id ?? `${g}-${e.ledger_code}`, isHeader: false })
    })
    // Subtotal row
    tableRows.push({
      key: `subtotal-${g}`,
      isSubtotal: true,
      ledger_name: `Sub-total — ${GROUP_TYPE_LABELS[g] ?? g}`,
      dr_balance: subtotalDR,
      cr_balance: subtotalCR,
    })
  })

  // Grand total row
  if (tableRows.length > 0) {
    tableRows.push({
      key: 'grand-total',
      isGrandTotal: true,
      ledger_name: 'GRAND TOTAL',
      dr_balance: totalDR,
      cr_balance: totalCR,
    })
  }

  const columns = [
    {
      title: 'Ledger Code', dataIndex: 'ledger_code', key: 'ledger_code', width: 120,
      render: (v: string, r: any) => {
        if (r.isHeader || r.isSubtotal || r.isGrandTotal) return null
        return <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</span>
      },
    },
    {
      title: 'Ledger / Group Name', dataIndex: 'ledger_name', key: 'ledger_name',
      render: (v: string, r: any) => {
        if (r.isGrandTotal) return <b style={{ fontSize: 14 }}>{v}</b>
        if (r.isSubtotal) return <i style={{ color: '#555' }}>{v}</i>
        if (r.isHeader) return <b style={{ fontSize: 13 }}>{v}</b>
        return v
      },
      onCell: (r: any) => ({
        style: {
          background: r.isGrandTotal ? '#1677ff' : r.isHeader ? '#e6f4ff' : r.isSubtotal ? '#f5f5f5' : 'transparent',
          color: r.isGrandTotal ? '#fff' : 'inherit',
          paddingLeft: r.isHeader || r.isSubtotal || r.isGrandTotal ? 8 : 24,
        },
      }),
    },
    {
      title: 'DR Balance (₹)', dataIndex: 'dr_balance', key: 'dr_balance', width: 160, align: 'right' as const,
      render: (v: number, r: any) => {
        if (v == null || v === 0) return null
        const formatted = fmtINR(v)
        if (r.isGrandTotal) return <b style={{ color: '#fff', fontSize: 14 }}>{formatted}</b>
        if (r.isSubtotal) return <b style={{ color: '#52c41a' }}>{formatted}</b>
        return <span style={{ color: '#52c41a' }}>{formatted}</span>
      },
      onCell: (r: any) => ({
        style: {
          background: r.isGrandTotal ? '#1677ff' : r.isHeader ? '#e6f4ff' : r.isSubtotal ? '#f5f5f5' : 'transparent',
        },
      }),
    },
    {
      title: 'CR Balance (₹)', dataIndex: 'cr_balance', key: 'cr_balance', width: 160, align: 'right' as const,
      render: (v: number, r: any) => {
        if (v == null || v === 0) return null
        const formatted = fmtINR(v)
        if (r.isGrandTotal) return <b style={{ color: '#fff', fontSize: 14 }}>{formatted}</b>
        if (r.isSubtotal) return <b style={{ color: '#ff4d4f' }}>{formatted}</b>
        return <span style={{ color: '#ff4d4f' }}>{formatted}</span>
      },
      onCell: (r: any) => ({
        style: {
          background: r.isGrandTotal ? '#1677ff' : r.isHeader ? '#e6f4ff' : r.isSubtotal ? '#f5f5f5' : 'transparent',
        },
      }),
    },
  ]

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 0 }}>
        <BarChartOutlined style={{ marginRight: 8, color: '#1677ff' }} />
        Trial Balance
      </Title>
      <Text type="secondary">Ledger-wise debit and credit balances for the selected period</Text>

      <Card style={{ marginTop: 16 }}>
        <Row gutter={12} style={{ marginBottom: 16 }} align="middle">
          <Col>
            <Select
              value={plantId}
              onChange={setPlantId}
              style={{ width: 180 }}
              options={plants.map((p: any) => ({ value: p.id, label: p.name ?? p.plant_name }))}
            />
          </Col>
          <Col>
            <DatePicker picker="month" value={period} onChange={v => v && setPeriod(v)} />
          </Col>
          <Col>
            <Select
              allowClear
              placeholder="Filter by Group"
              style={{ width: 200 }}
              value={groupFilter}
              onChange={setGroupFilter}
              options={GROUP_TYPE_ORDER.map(g => ({ value: g, label: GROUP_TYPE_LABELS[g] ?? g }))}
            />
          </Col>
          <Col>
            <Button type="primary" onClick={handleGenerate} loading={isLoading}>
              Generate
            </Button>
          </Col>
          {entries.length > 0 && (
            <Col>
              <Button
                icon={<DownloadOutlined />}
                onClick={() => exportCSV(entries, `trial-balance-${period?.format('YYYY-MM')}.csv`)}
              >
                Export CSV
              </Button>
            </Col>
          )}
        </Row>

        {triggered && entries.length > 0 && !isBalanced && (
          <Alert
            type="warning"
            message={`Trial balance is NOT balanced. DR: ₹${totalDR.toFixed(2)} | CR: ₹${totalCR.toFixed(2)} | Diff: ₹${Math.abs(totalDR - totalCR).toFixed(2)}`}
            style={{ marginBottom: 16 }}
            showIcon
          />
        )}

        {triggered && entries.length > 0 && isBalanced && (
          <Alert
            type="success"
            message={`Trial balance is balanced — Total: ${fmtINR(totalDR)}`}
            style={{ marginBottom: 16 }}
            showIcon
          />
        )}

        <Table
          dataSource={tableRows}
          columns={columns}
          rowKey="key"
          loading={isLoading}
          size="small"
          pagination={false}
          scroll={{ x: 660, y: 600 }}
          locale={{ emptyText: triggered ? 'No data for the selected period' : 'Click Generate to load trial balance' }}
          rowClassName={(r: any) => {
            if (r.isGrandTotal) return 'tb-grand-total'
            if (r.isHeader) return 'tb-group-header'
            if (r.isSubtotal) return 'tb-subtotal'
            return ''
          }}
        />

        {entries.length > 0 && (
          <Row style={{ marginTop: 12 }}>
            <Col span={12}>
              <Tag color="green">Total DR: {fmtINR(totalDR)}</Tag>
            </Col>
            <Col span={12} style={{ textAlign: 'right' }}>
              <Tag color="red">Total CR: {fmtINR(totalCR)}</Tag>
            </Col>
          </Row>
        )}
      </Card>
    </div>
  )
}
